import { Box, HStack, VStack, Text, Button } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useSubscription } from '../hooks/useSubscription';
import { getSubscriptionBannerVariant } from '../utils/subscriptionBanner';

interface SubscriptionStatusBannerProps {
  onManage: () => void;
}

/**
 * Post-trial enforcement banner (FR-005a / FR-058), the web counterpart of the
 * mobile SubscriptionStatusBanner:
 *  - grace (Restricted): "trial ended — add payment" with a manage CTA;
 *  - locked: "access locked — subscribe to continue".
 * Renders nothing for trial/none (the trial banner is surfaced elsewhere).
 */
export function SubscriptionStatusBanner({ onManage }: SubscriptionStatusBannerProps) {
  const { t } = useTranslation();
  const { isTrialing, accessStatus, daysRemaining } = useSubscription();
  const variant = getSubscriptionBannerVariant({ isTrialing, accessStatus });

  if (variant !== 'grace' && variant !== 'locked') return null;

  const isLocked = variant === 'locked';
  const prefix = isLocked ? 'subscription.locked' : 'subscription.grace';

  return (
    <Box
      bg="onboarding.plum"
      color="onboarding.white"
      px={{ base: 4, md: 6 }}
      py={3}
      data-testid={isLocked ? 'subscription-locked-banner' : 'subscription-grace-banner'}
    >
      <HStack
        maxW="1200px"
        mx="auto"
        spacing={4}
        align={{ base: 'stretch', md: 'center' }}
        justify="space-between"
        flexDir={{ base: 'column', md: 'row' }}
      >
        <VStack align="start" spacing={1}>
          <Text fontWeight="600" fontSize="md">
            {t(`${prefix}.bannerTitle`, { days: daysRemaining })}
          </Text>
          <Text fontSize="sm" color="onboarding.roseFaint">
            {t(`${prefix}.bannerDescription`)}
          </Text>
        </VStack>
        <Button
          flexShrink={0}
          size="sm"
          bg="onboarding.cream"
          color="onboarding.plum"
          borderRadius="full"
          _hover={{ bg: 'onboarding.warmLight' }}
          onClick={onManage}
          data-testid="subscription-status-cta"
        >
          {t(`${prefix}.cta`)}
        </Button>
      </HStack>
    </Box>
  );
}

export default SubscriptionStatusBanner;
